import React from 'react';
import _ from 'underscore';

import {assertAndContinue} from 'Asserts';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import {FolderPreferences, FolderSection, StringIDFolder} from 'constants/FolderConstants';
import * as NavConstants from 'constants/NavConstants';
import FinderUtil from 'file-browser/FinderUtil';
import FolderCreateDropdown from 'file-browser/FolderCreateDropdown';
import normalizeFolder from 'folders/util/normalizeFolder';
import {SearchSection} from 'graphql-client/__generated/graphqlGlobalTypes';
import {getFolderListingURL} from 'nav/sidepanel/listing/SearchURLs';
import notifications from 'notifications';
import preferences from 'Preferences';
import {getFilterKeysForModelType} from 'search/FiltersByType';
import * as FilterUtil from 'search/FilterUtil';
import {
  Filter,
  FilterKey,
  keyToEmptyFilter,
  SearchParams,
  SearchType,
  SingleValueFilter,
  SortKey,
} from 'search/SearchConstants';
import * as SearchUtil from 'search/SearchUtil';
import withSearching, {SearchProps} from 'search/withSearching';

import FolderListingCrumbs from './FolderListingCrumbs';
import ItemListing from './ItemListing';
import {
  getDefaultProjectItemsSearchParams,
  getDefaultTypesForSection,
  INVENTORY_FILTER,
  NOTEBOOK_FILTER,
  PRESERVED_FILTER_KEYS,
  resolveFolderFilter,
} from './ProjectsSearchParams';

interface ProjectsSidepanelListingProps {
  isCompactDisplay: boolean;
  setIsCompactDisplay: (isCompact: boolean) => void;
  isExpanded: boolean;
  activeItemId: string | null;
  onNavigateToRoot: () => void;
  onOpenFolder: (folder: StringIDFolder, searchParams?: SearchParams) => void;
  folder: StringIDFolder;
}

function assertAllTypesSupportRequiredFilters(types: Array<string>): void {
  // IF THIS ASSERTION FAILS:
  // Projects listing adds hidden folder reltaed filters as you drill through the folder hierarchy. All
  // types must support these filters, or they'll get filtered out by sanitizeFilters, breaking this
  // listing's behavior. To fix this, make sure both of the following hold:
  // 1. The filter key(s) are present in FiltersByType for all relevant models.
  // 2. The associated DocumentMappers includes support for these filters on the back end.
  const missingTypes: Array<string> = [];
  assertAndContinue(
    types.every((type) => {
      const filterKeys = getFilterKeysForModelType(type);
      const hasFilters =
        filterKeys.includes(FilterKey.ANCESTOR_FOLDER) &&
        filterKeys.includes(FilterKey.FOLDER) &&
        filterKeys.includes(FilterKey.FOLDER_SECTION);
      if (!hasFilters) {
        missingTypes.push(type);
      }
      return hasFilters;
    }),
    `All availableTypes for ProjectsSidepanelListing must support the ANCESTOR_FOLDER, FOLDER, and FOLDER_SECTION filters. Types missing filters: ${missingTypes}`
  );
}

export class ProjectsSidepanelListing extends React.Component<ProjectsSidepanelListingProps & SearchProps> {
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  _handleSearch = (searchParams: SearchParams) => {
    const newSearchParams = resolveFolderFilter(searchParams);
    this.props.onSearch(newSearchParams);
  };

  // TODO(Search): resolveFolderFilter is filter logic that depends on multiple search parameters and is
  // specific to this listing. This is an exception to the normal listing behavior that is able to be
  // moved to the reducers. We're hacking around by merging the new filter in based on the full props,
  // and possibly performing multple dispatches, but this is not a great long term solution. Revisit this
  // once we are fully migrated to the new granular actions and have time to consider a better design.
  _handleUpdateFilters = (filters: Array<Filter>): void => {
    const searchParams = {...this.props.searchParams, filters};
    const newSearchParams = resolveFolderFilter(searchParams);
    this.props.onUpdateFilters(newSearchParams.filters);
  };

  _handleUpdateQueryString = (query: string): void => {
    const searchParams = {...this.props.searchParams, query};
    const newSearchParams = resolveFolderFilter(searchParams);
    this.props.onUpdateQueryString(newSearchParams.query);
    this.props.onUpdateFilters(newSearchParams.filters);
  };

  _getSearchResultsSubtext = (): string | null => {
    if (
      this.props.searchParams.filters.some(
        (filter) => filter.key === FilterKey.ANCESTOR_FOLDER && filter.value != null
      )
    ) {
      return 'including items in subfolders';
    }
    return null;
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  _handleChangeSection = (section: FolderSection) => {
    // If section didn't change, no-op.
    if (
      section === FilterUtil.getFilterValueByKey(this.props.searchParams.filters, FilterKey.FOLDER_SECTION)
    ) {
      return;
    }
    // If loading, no-op.
    if (this.props.folder == null) {
      notifications.warn('Please wait until folder is loaded before switching sections.');
      return;
    }
    preferences.set(FolderPreferences.DEFAULT_SECTION, section);
    const sectionFilter = section === FolderSection.INVENTORY ? INVENTORY_FILTER : NOTEBOOK_FILTER;
    const rootFolder = FinderUtil.sourceToRootFolder(this.props.folder.source!);
    const projectSearchParams = getDefaultProjectItemsSearchParams(rootFolder);
    this.props.onOpenFolder(
      rootFolder,
      SearchUtil.mergeFiltersToSearchParams(projectSearchParams, [
        sectionFilter,
        {
          ...keyToEmptyFilter(FilterKey.TYPES),
          value: getDefaultTypesForSection(section),
        },
      ])
    );
  };

  render(): JSX.Element {
    const sectionFilter = FilterUtil.getFilterByKey(
      this.props.searchParams.filters,
      FilterKey.FOLDER_SECTION
    ) as SingleValueFilter;
    assertAndContinue(Boolean(sectionFilter), 'Expected sectionFilter to exist in projects listing');
    const defaultSection = FolderSection.ALL;
    const section = sectionFilter ? (sectionFilter.value as FolderSection) : defaultSection;
    const availableTypesForSection = getDefaultTypesForSection(section);

    assertAllTypesSupportRequiredFilters(availableTypesForSection);
    return (
      <div className="sidepanel">
        <ItemListing
          actionSource={ActionOrigin.PROJECTS_SIDEPANEL}
          savedSearchSection={SearchSection.PROJECT_ITEMS}
          // eslint-disable-next-line no-restricted-syntax
          {..._.omit(this.props, 'folder')}
          onSearch={this._handleSearch}
          onUpdateFilters={this._handleUpdateFilters}
          onUpdateQueryString={this._handleUpdateQueryString}
          availableSortKeys={[
            SortKey.SORT_NAME_KEY,
            SortKey.SORT_MODIFIED_AT_KEY,
            SortKey.SORT_CREATED_AT_KEY,
          ]}
          availableTypes={availableTypesForSection}
          sectionTabs={undefined}
          hideSectionTabsWhenExpanded={true}
          // eslint-disable-next-line react/no-unstable-nested-components -- Disabling for now, but worth fixing
          makeCrumbs={(settingsComponents?: JSX.Element) => (
            <FolderListingCrumbs
              folderId={normalizeFolder(this.props.folder).id}
              onNavigateToRoot={this.props.onNavigateToRoot}
              sectionName="Projects"
              showSectionDropdown={false}
              section={section}
              searchParams={this.props.searchParams}
              onChangeSection={this._handleChangeSection}
              onOpenFolder={this.props.onOpenFolder}
              activeSavedSearch={this.props.activeSavedSearch}
              settingsComponents={settingsComponents}
            />
          )}
          additionalHeaderButtons={
            <FolderCreateDropdown
              dropLeft={this.props.isExpanded}
              folder={this.props.folder}
              onOpenFolder={this.props.onOpenFolder}
            />
          }
          preservedFilterKeys={PRESERVED_FILTER_KEYS}
          extraPrimaryFilterOptions={[]}
          section={NavConstants.Section.PROJECT_ITEMS}
          searchResultsSubtext={this._getSearchResultsSubtext()}
        />
      </div>
    );
  }
}

export default withSearching(
  ProjectsSidepanelListing,
  SearchType.FOLDERS_AND_ITEMS,
  NavConstants.SECTIONS.PROJECT_ITEMS,
  {getNavigationUpdate: getFolderListingURL}
);
